import React from 'react';
import cn from 'classnames';
import Div100vh from 'react-div-100vh';
import { Redirect, useParams, generatePath } from 'react-router-dom';

import { useTranslation, useAppSelector, useResponsive } from 'client/hooks';
import { companies as companiesApi } from 'client/redux/api/companies';

import { NOT_FOUND_PAGE_ROUTE } from 'client/constants/routes';

import { imgixUrl } from 'client/utils/imgix-url';

import { ControlPanel, Header } from 'client/components/common/mobile';
import { HelmetComponent, Loader, Icon } from 'client/components/common';
import { selectLang } from 'client/redux/settings/selectors';

import defaultMemberPhoto from 'client/assets/default-avatar.svg';

import css from './CompanyTeamMember.module.scss';

const IMGIX_PARAMS = {
  avatar: {
    width: 243,
    height: 243,
    fit: 'fill',
    auto: 'format,compress',
    q: 70,
    crop: 'entropy',
  },
  bgImage: {
    width: 1200,
    height: 'auto',
    fit: 'fill',
    auto: 'format,compress',
    q: 70,
    crop: 'entropy',
  },
};

const CompanyTeamMember = () => {
  const { translate } = useTranslation();
  const lang = useAppSelector(selectLang);
  const [isMobile] = useResponsive(['MOBILE']);
  const routeParams = useParams<{ id: string; slug: string }>();
  const [slug] = React.useState(routeParams.slug);
  const [companyId] = React.useState(routeParams.id);

  const { data, isLoading } = companiesApi.endpoints.getCompany.useQuery({
    id: companyId || '',
  });
  const teamMember = React.useMemo(
    () => (data?.success ? data.data?.team.find((t) => t.slug === slug) : undefined),
    [data, slug]
  );

  const otherInfo = React.useMemo(() => {
    return [
      { title: translate('companyTeamMember.roleTitle'), value: teamMember?.role },
      { title: translate('companyTeamMember.orgTitle'), value: teamMember?.organization },
      { title: translate('companyTeamMember.locTitle'), value: teamMember?.location },
      { title: translate('companyTeamMember.websiteTitle'), value: teamMember?.website, isValueLink: true },
    ].filter((item) => Boolean(item.value));
  }, [teamMember, translate]);

  const atSomeOrgLabel = teamMember?.organization
    ? `${translate('companyTeamMember.at')} ${teamMember?.organization}`
    : '';

  const ldJsonData = React.useMemo(() => {
    if (!teamMember) return '';

    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: teamMember.name,
      jobTitle: teamMember.role,
      worksFor: {
        '@type': 'Organization',
        name: teamMember.organization,
      },
      url: normalizeUrl(teamMember.website ?? ''),
      sameAs: [
        [
          normalizeUrl(teamMember.twitterLink ?? ''),
          normalizeUrl(teamMember.linkedinLink ?? ''),
          normalizeUrl(teamMember.facebookLink ?? ''),
        ].filter(Boolean),
      ],
      image: teamMember?.avatar ? imgixUrl(teamMember.avatar, IMGIX_PARAMS.avatar) : defaultMemberPhoto,
      address: {
        '@type': 'PostalAddress',
        addressLocality: teamMember.location,
      },
    });
  }, [teamMember]);

  if (!teamMember && !isLoading) {
    return <Redirect to={generatePath(NOT_FOUND_PAGE_ROUTE, { lang })} />;
  }

  const renderOtherInfo = () => {
    return otherInfo.map((other) => (
      <div key={other.value} className={css.field}>
        <div className={css.fieldInner}>
          <div className={css.label}>{other.title}</div>
          <div className={css.value}>
            {other.isValueLink ? (
              <a href={normalizeUrl(other.value ?? '')} target="_blank" rel="noreferrer">
                {other.value}
              </a>
            ) : (
              other.value
            )}
          </div>
        </div>
      </div>
    ));
  };

  const renderPageContent = () => {
    return (
      <div className={css.companyTeamMember}>
        {isLoading ? (
          <Loader position="absolute" />
        ) : (
          <>
            <div
              className={css.blurredBg}
              style={{
                backgroundImage: `url('${imgixUrl(teamMember?.avatar, IMGIX_PARAMS.bgImage) || defaultMemberPhoto}')`,
              }}
            />
            <div className={css.content}>
              <div className={css.person}>
                <div className={css.nameAndAvatar}>
                  <img
                    src={imgixUrl(teamMember?.avatar, IMGIX_PARAMS.avatar) || defaultMemberPhoto}
                    className={css.avatar}
                    alt={teamMember?.name}
                  />
                  <div className={css.name}>
                    <h4>{teamMember?.name}</h4>
                    <p>{`${teamMember?.role} ${atSomeOrgLabel}`}</p>
                  </div>
                </div>
                <div className={css.socials}>
                  {teamMember?.twitterLink && (
                    <a href={normalizeUrl(teamMember.twitterLink)} target="_blank" rel="noreferrer">
                      <Icon className={cn(css.icon, css.twitter)} type="twitter-2" />
                    </a>
                  )}
                  {teamMember?.facebookLink && (
                    <a href={normalizeUrl(teamMember.facebookLink)} target="_blank" rel="noreferrer">
                      <Icon className={cn(css.icon, css.fb)} type="facebook-2" />
                    </a>
                  )}
                  {teamMember?.linkedinLink && (
                    <a href={normalizeUrl(teamMember.linkedinLink)} target="_blank" rel="noreferrer">
                      <Icon className={cn(css.icon, css.linkedIn)} type="linkedin-2" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className={cn(css.content, css.mainInfo)}>
              <div className={css.title}>{translate('companyTeamMember.overviewTitle')}</div>
              <div className={css.descriptionWrapper}>
                <div className={css.text}>{teamMember?.description}</div>
                <div className={css.memberCard}>{renderOtherInfo()}</div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <HelmetComponent
        title={translate('seo.companyTeamMember.title', { name: teamMember?.name ?? '' })}
        description={translate('seo.companyTeamMember.description', {
          name: teamMember?.name ?? '',
        })}
        ogTitle={translate('seo.companyTeamMember.title', { name: teamMember?.name ?? '' })}
        ogDescription={translate('seo.companyTeamMember.description', {
          name: teamMember?.name ?? '',
        })}
        ogImage={teamMember?.avatar ? `${imgixUrl(teamMember?.avatar, IMGIX_PARAMS.avatar)}` : defaultMemberPhoto}
        ldJsonData={ldJsonData}
      />
      {isMobile && (
        <>
          <Header className={css.header} id="company-team-member" />
          <ControlPanel className={css.controlPanel} />
        </>
      )}
      {isMobile ? <Div100vh className={css.contentWrap}>{renderPageContent()}</Div100vh> : renderPageContent()}
    </>
  );
};

function normalizeUrl(str: string): string {
  if (/^(https?:\/\/|www\.)/i.test(str)) {
    return str;
  }
  return `https://${str}`;
}

export default CompanyTeamMember;
